import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { Section } from '@/shared/ui/layout/Section';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { usePerm } from '@/shared/hooks/usePerm';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { apiFaqDelete, apiFaqPut, useFaqs } from '@/entities/faq';
import { FeaturesFaqsFaqFormDialog } from '@/features/faqs';
import type { ApiFaqGetItem } from '@/shared/types/api/faqs';
import { cn } from '@/shared/utils/cn';

export const WidgetsFaqsMain = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const canCreate = usePerm('faqs.create');
  const canEdit = usePerm('faqs.edit');
  const canDelete = usePerm('faqs.delete');
  const query = useFaqs();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ApiFaqGetItem | null>(null);
  const [toDelete, setToDelete] = useState<ApiFaqGetItem | null>(null);
  const [toToggle, setToToggle] = useState<ApiFaqGetItem | null>(null);

  const toggleActive = useMutate({
    api: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiFaqPut(id, { is_active: isActive }),
    invalidateKeys: [queryKeys.faqs.all],
    onSuccess: (_d, vars) => {
      toast.success(vars.isActive ? t('faqs.toast.activated') : t('faqs.toast.deactivated'));
      setToToggle(null);
    },
    onError: (_e, m) => toast.error(m),
  });

  const remove = useMutate({
    api: (id: string) => apiFaqDelete(id),
    invalidateKeys: [queryKeys.faqs.all],
    onSuccess: () => {
      toast.info(t('faqs.toast.deleted'));
      setToDelete(null);
    },
    onError: (_e, m) => toast.error(m),
  });

  return (
    <>
      <PageHeader
        title={t('faqs.list.title')}
        subtitle={t('faqs.list.subtitle')}
        right={
          canCreate ? (
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
              title={t('faqs.list.newFaq')}
              aria-label={t('faqs.list.newFaq')}
            >
              <span className="hidden sm:inline">{t('faqs.list.newFaq')}</span>
            </Button>
          ) : null
        }
      />

      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        {query.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('faqs.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : query.data?.length === 0 ? (
          <EmptyState
            icon={<HelpCircle className="h-8 w-8" />}
            title={t('faqs.list.emptyTitle')}
            description={t('faqs.list.emptyDescription')}
          />
        ) : (
          <div className="space-y-2">
            {(query.data ?? []).map((faq) => (
              <Section key={faq.id} className="!p-0">
                <div className="flex items-center gap-3 px-4 py-3">
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-100 text-sm font-semibold text-ink-soft">
                    {faq.order}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-ink">{faq.question_uz}</p>
                    <p className="truncate text-xs text-ink-muted">{faq.answer_uz}</p>
                  </div>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                      faq.is_active
                        ? 'border-emerald-200 bg-emerald-100 text-emerald-800'
                        : 'border-slate-200 bg-slate-100 text-slate-700',
                    )}
                  >
                    {faq.is_active ? t('faqs.list.active') : t('faqs.list.inactive')}
                  </span>
                  {canEdit && (
                    <IconButton
                      label={faq.is_active ? t('faqs.list.deactivate') : t('faqs.list.activate')}
                      disabled={toggleActive.isPending}
                      onClick={() => setToToggle(faq)}
                    >
                      <Power
                        className={cn('h-4 w-4', faq.is_active ? 'text-amber-600' : 'text-emerald-600')}
                      />
                    </IconButton>
                  )}
                  {canEdit && (
                    <IconButton
                      label={t('common.edit')}
                      onClick={() => {
                        setEditing(faq);
                        setDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </IconButton>
                  )}
                  {canDelete && (
                    <IconButton
                      label={t('common.delete')}
                      variant="danger"
                      onClick={() => setToDelete(faq)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </IconButton>
                  )}
                </div>
              </Section>
            ))}
          </div>
        )}
      </div>

      <FeaturesFaqsFaqFormDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />

      <ConfirmDialog
        open={Boolean(toDelete)}
        title={t('faqs.confirmDelete.title')}
        description={t('faqs.confirmDelete.description', { name: toDelete?.question_uz ?? '' })}
        confirmLabel={t('faqs.confirmDelete.confirm')}
        confirmVariant="danger"
        loading={remove.isPending}
        onCancel={() => setToDelete(null)}
        onConfirm={() => toDelete && remove.mutate(toDelete.id)}
      />

      <ConfirmDialog
        open={Boolean(toToggle)}
        title={
          toToggle?.is_active
            ? t('faqs.confirmToggle.deactivateTitle')
            : t('faqs.confirmToggle.activateTitle')
        }
        description={
          toToggle?.is_active
            ? t('faqs.confirmToggle.deactivateDescription', { name: toToggle?.question_uz ?? '' })
            : t('faqs.confirmToggle.activateDescription', { name: toToggle?.question_uz ?? '' })
        }
        confirmLabel={
          toToggle?.is_active
            ? t('faqs.confirmToggle.deactivateConfirm')
            : t('faqs.confirmToggle.activateConfirm')
        }
        loading={toggleActive.isPending}
        onCancel={() => setToToggle(null)}
        onConfirm={() =>
          toToggle && toggleActive.mutate({ id: toToggle.id, isActive: !toToggle.is_active })
        }
      />
    </>
  );
};
